import React, { Component } from "react";
import { Modal, ModalBody, Button } from "reactstrap";
import Row from "reactstrap/lib/Row";
import Col from "reactstrap/lib/Col";
import Icon from "@material-ui/core/Icon";
import ReactDatePicker from "react-datepicker";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import ApiEndpoints from "../../../constants/api_endpoints";
import moment from "moment";

const LABEL = {
    fontSize: 11, fontWeight: 700, letterSpacing: "0.6px",
    textTransform: "uppercase", color: "#666", marginBottom: 6, display: "block",
};

const STAT_CARDS = [
    { key: "impressions",   label: "Impressions",  icon: "visibility",    color: "#1565c0" },
    { key: "completed",     label: "Completed",    icon: "task_alt",      color: "#2e7d32" },
    { key: "avg_watch_pct", label: "Avg Watch",    icon: "show_chart",    color: "#e65100", suffix: "%" },
    { key: "likes",         label: "Likes",        icon: "thumb_up",      color: "#c62828" },
    { key: "feedback_count",label: "Feedback",     icon: "chat_bubble",   color: "#6a1b9a" },
];

class VideoMetricsModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            data: null,
            loading: false,
            start: null,
            end: null,
        };
    }

    componentDidUpdate(prevProps) {
        if (this.props.video && !prevProps.video) {
            const start = moment().subtract(30, "days").toDate();
            const end = moment().toDate();
            this.setState({ start, end, data: null }, () => this.fetchData());
        }
    }

    fetchData = async () => {
        const { video } = this.props;
        const { start, end } = this.state;
        if (!video) return;
        this.setState({ loading: true });
        try {
            const resp = await ApiEndpoints.Videos.fetchVideoMetrics({
                video_id: video.video_id,
                ...(start ? { start_date: moment(start).format("YYYY-MM-DD") } : {}),
                ...(end   ? { end_date:   moment(end).format("YYYY-MM-DD")   } : {}),
            });
            this.setState({ data: resp.data.data, loading: false });
        } catch (e) {
            this.setState({ loading: false });
        }
    }

    render() {
        const { video, onClose } = this.props;
        const { data, loading, start, end } = this.state;

        return (
            <Modal isOpen={!!video} toggle={onClose} size="lg" centered>
                <div style={{
                    background: "#f1061c", borderRadius: "6px 6px 0 0",
                    padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between",
                }}>
                    <span style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
                        Video Metrics — {video && video.title}
                    </span>
                    <button type="button" onClick={onClose}
                        style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", padding: 4 }}>
                        <Icon style={{ fontSize: 20 }}>close</Icon>
                    </button>
                </div>

                <ModalBody style={{ padding: "20px 24px 24px" }}>

                    {/* All-time stat cards */}
                    <div style={{ display: "flex", gap: 10, marginBottom: 24 }}>
                        {STAT_CARDS.map(s => (
                            <div key={s.key} style={{
                                flex: 1, background: "#fafafa", border: "1px solid #f0f0f0",
                                borderRadius: 8, padding: "12px 8px", textAlign: "center", minWidth: 0,
                            }}>
                                <Icon style={{ fontSize: 18, color: s.color, display: "block", margin: "0 auto 4px" }}>{s.icon}</Icon>
                                <div style={{ fontSize: 20, fontWeight: 800, color: "#222", lineHeight: 1.2 }}>
                                    {loading || !data ? "—" : `${data[s.key] || 0}${s.suffix || ""}`}
                                </div>
                                <div style={{ fontSize: 10, color: "#888", marginTop: 3, textTransform: "uppercase", letterSpacing: "0.4px", fontWeight: 600 }}>
                                    {s.label}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Date Filters for daily graph */}
                    <Row className="mb-3 align-items-end">
                        <Col xs={5}>
                            <label style={LABEL}>From</label>
                            <ReactDatePicker
                                selected={start}
                                maxDate={end || new Date()}
                                onChange={date => this.setState({ start: date }, () => this.fetchData())}
                                isClearable dateFormat="dd-MMM-yyyy"
                                placeholderText="Start date"
                                className="form-control" wrapperClassName="d-block"
                            />
                        </Col>
                        <Col xs={5}>
                            <label style={LABEL}>To</label>
                            <ReactDatePicker
                                selected={end}
                                minDate={start}
                                maxDate={new Date()}
                                onChange={date => this.setState({ end: date }, () => this.fetchData())}
                                isClearable dateFormat="dd-MMM-yyyy"
                                placeholderText="End date"
                                className="form-control" wrapperClassName="d-block"
                            />
                        </Col>
                        <Col xs={2}>
                            <Button size="sm" type="button" onClick={this.fetchData} style={{
                                background: "#f1061c", border: "none", color: "#fff",
                                fontWeight: 600, borderRadius: 6, width: "100%",
                            }}>
                                <Icon style={{ fontSize: 14, verticalAlign: "middle" }}>refresh</Icon>
                            </Button>
                        </Col>
                    </Row>

                    {/* Multi-line daily graph */}
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10 }}>
                        Daily Trend
                    </div>
                    {loading ? (
                        <div style={{ textAlign: "center", padding: "40px 0", color: "#aaa", fontSize: 13 }}>
                            <Icon style={{ fontSize: 28, display: "block", margin: "0 auto 8px", color: "#e0e0e0" }}>show_chart</Icon>
                            Loading…
                        </div>
                    ) : data && data.daily && data.daily.length ? (
                        <ResponsiveContainer width="100%" height={240}>
                            <LineChart data={data.daily} margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                                <XAxis dataKey="date" tick={{ fontSize: 11 }}
                                    tickFormatter={d => moment(d).format("D MMM")} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
                                <Tooltip
                                    labelFormatter={label => moment(label).format("D MMM YYYY")}
                                    contentStyle={{ fontSize: 12, borderRadius: 6 }}
                                />
                                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                                <Line type="monotone" dataKey="impressions" stroke="#1565c0" strokeWidth={2} dot={false} activeDot={{ r: 4 }} name="Impressions" />
                                <Line type="monotone" dataKey="completed"   stroke="#2e7d32" strokeWidth={2} dot={false} activeDot={{ r: 4 }} name="Completed" />
                                <Line type="monotone" dataKey="likes"       stroke="#c62828" strokeWidth={2} dot={false} activeDot={{ r: 4 }} name="Likes" />
                                <Line type="monotone" dataKey="feedback"    stroke="#6a1b9a" strokeWidth={2} dot={false} activeDot={{ r: 4 }} name="Feedback" />
                            </LineChart>
                        </ResponsiveContainer>
                    ) : (
                        <div style={{ textAlign: "center", padding: "40px 0", color: "#aaa", fontSize: 13 }}>
                            <Icon style={{ fontSize: 28, display: "block", margin: "0 auto 8px", color: "#e0e0e0" }}>show_chart</Icon>
                            No data in this date range.
                        </div>
                    )}
                </ModalBody>
            </Modal>
        );
    }
}

export default VideoMetricsModal;
